import { SubmissionError } from '#helper/submission-error';
import {
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER,
  FETCH_APPLIANCE_LOCATIONS,
  APPLIANCE_DEVICES_FETCH_STARTED,
  APPLIANCE_DEVICES_FETCH_SUCCEEDED,
  ALL_APPLIANCE_DEVICES_FETCH_SUCCEEDED,
  APPLIANCE_DEVICES_FETCH_FAILED,
  APPLIANCE_DEVICES_CLEAR,
  CLOSE_BACKDROP,
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_STARTED,
  FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_COMPLETED,
  UPDATE_APPLIANCE_EVENTS,
  ERROR
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError, fetchAdditions } from '#action/fetch';
import { getRequestOptions, addParamsToUri, getDateRange, chunkArray } from '#helper';
import { history } from '#configureStore';

let abortController = new AbortController();

export const getApplianceDevices = async (id, dispatch, period) => {
  try {
    abortController.abort();
    let applianceDevicesUrl = webPortalApi.buildApiUrl(`warehouse/appliance/${id}/device`);
    if (period) {
      const { start, end } = period;
      const range = getDateRange(start, end);
      applianceDevicesUrl += `/${range.start}/${range.end}`;
    }
    dispatch({
      type: APPLIANCE_DEVICES_FETCH_STARTED
    });
    abortController = new AbortController();
    const { signal } = abortController;
    const res = await fetch(applianceDevicesUrl, { ...getRequestOptions(), signal });
    await dispatch({ type: CLOSE_BACKDROP });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    const response = await res.json();

    if (!res.ok) {
      const shallRedirect = [403, 404].includes(response.resStatus);

      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );

      if (shallRedirect) {
        await history.push('/warehouse');
      }

      throw response;
    }

    return response;
  } catch (e) {
    if (e.name === 'AbortError') {
      return null;
    }
    dispatch({
      type: APPLIANCE_DEVICES_FETCH_FAILED,
      error: e
    });
    return null;
  }
};

export const fetchDeviceDetails = async (ids, multipleFetch) => {
  if (!multipleFetch) {
    abortController.abort();
  }

  const applianceDevicesIds = [...new Set(ids)].join(',');
  const deviceDetailsUrl = webPortalApi.buildApiUrl(`device/distinct/details?ids=${applianceDevicesIds}`);
  abortController = new AbortController();
  const { signal } = abortController;
  const res = await fetch(deviceDetailsUrl, { ...getRequestOptions(), signal });
  const contentType = res.headers.get('content-type');

  if (!contentType || !contentType.includes('application/json')) {
    throw new TypeError('Response is not json');
  }
  if (!res.ok) {
    throw await res.json();
  }
  const deviceDetails = await res.json();
  return deviceDetails.reduce((acc, rec) => ({ ...acc, [rec.id]: rec }), {});
};

export const fetchApplianceDevices = (id, startDate, endDate) => async dispatch => {
  dispatch({
    type: APPLIANCE_DEVICES_FETCH_STARTED
  });
  let period = null;
  if (startDate && endDate) {
    period = { start: startDate, end: endDate };
  }

  const appliancesWithDevices = await getApplianceDevices(id, dispatch, period);

  if (!appliancesWithDevices?.length) {
    return false;
  }

  return dispatch({
    type: APPLIANCE_DEVICES_FETCH_SUCCEEDED,
    payload: appliancesWithDevices
  });
};

export const fetchApplianceDevicesDetailed = (
  id,
  allDevices = false,
  defaultAppliancesWithDevices,
  alerts
) => async dispatch => {
  const appliancesWithDevices = defaultAppliancesWithDevices ||
    await getApplianceDevices(id, dispatch);

  if (!appliancesWithDevices?.length) {
    dispatch({
      type: allDevices ? ALL_APPLIANCE_DEVICES_FETCH_SUCCEEDED :
        APPLIANCE_DEVICES_FETCH_SUCCEEDED,
      payload: []
    });

    return;
  }

  dispatch({
    type: APPLIANCE_DEVICES_FETCH_STARTED
  });

  const deviceIds = appliancesWithDevices?.reduce((acc, rec) =>
    acc.concat(rec.devices.map(d => d.id)), []);

  const MAX_DEVICES_PER_REQUEST = 50;
  let deviceDetails;

  if (deviceIds.length <= MAX_DEVICES_PER_REQUEST) {
    deviceDetails = await fetchDeviceDetails(deviceIds);
  } else {
    const devicesChunks = chunkArray(deviceIds, MAX_DEVICES_PER_REQUEST);
    const responses = await Promise.all(
      devicesChunks.map(chunk => fetchDeviceDetails(chunk, true))
    );
    deviceDetails = responses?.reduce((acc, curr) => ({ ...acc, ...curr }), {});
  }

  const appliancesWithDeviceDetails = appliancesWithDevices.map(a =>
    ({ ...a,
      devices:
        a.devices.map(device => ({
          ...device,
          ...deviceDetails[device.id],
          groupLabel: a.label,
          groupAlertId: a.projectAlertId,
          alerts: alerts?.filter(al => al.applianceGroupId === a.id) || []
        }))
    }));

  await dispatch({
    type: allDevices ? ALL_APPLIANCE_DEVICES_FETCH_SUCCEEDED : APPLIANCE_DEVICES_FETCH_SUCCEEDED,
    payload: appliancesWithDeviceDetails
  });
};

export const applianceDevicesClear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: APPLIANCE_DEVICES_CLEAR
  });
};

export const putApplianceDevices = (id, devicesGroup) => async dispatch => {
  try {
    const applianceDevicesUrl = webPortalApi.buildApiUrl(`warehouse/appliance/${id}/device`);
    const res = await fetch(applianceDevicesUrl, {
      method: 'PUT',
      body: JSON.stringify(devicesGroup),
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    const response = await res.json();

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );
      throw new SubmissionError(response);
    }

    return { success: true };
  } catch (e) {
    return { success: false };
  }
};

function getUriApplianceMeasurements(id, period, path) {
  let URI = `warehouse/appliance/${id}/${path}`;

  if (period) {
    const { start, end } = period;
    const range = getDateRange(start, end);
    URI += `/${range.start}/${range.end}`;
  }
  return URI;
}

const getUriApplianceParams = (id, period, path, params) => {
  const uri = getUriApplianceMeasurements(id, period, path);
  return addParamsToUri(uri, params);
};

export const fetchMetricsByIdentifier = (id, period, identifier) =>
  dispatch => {
    dispatch({
      type: FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_STARTED,
      payload: identifier
    });

    return dispatch(fetchAdditions(
      getUriApplianceParams(id, period, 'metrics', { metric: identifier }),
      FETCH_APPLIANCE_METRICS_BY_IDENTIFIER,
      { identifier }
    )).then(r => {
      if (r.type === ERROR) {
        return false;
      }
      return dispatch({
        type: FETCH_APPLIANCE_METRICS_BY_IDENTIFIER_COMPLETED,
        payload: identifier
      });
    });
  };

export const fetchLocations = (id, period, withoutMetrics = false) => dispatch =>
  dispatch(fetchAdditions(
    getUriApplianceParams(id, period, 'locations', { withoutMetrics }),
    FETCH_APPLIANCE_LOCATIONS
  ));

export const updateApplianceEvents = data => dispatch => {
  dispatch({ type: UPDATE_APPLIANCE_EVENTS, payload: data });
};
